import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { BottomNav } from "@/components/bottom-nav";
import { FocusBar } from "@/components/FocusBar";
import { useProfileFlags } from "@/hooks/use-profile-flags";
import { useTomorrowHomeworkReminder } from "@/components/ClassScheduleSection";
import { useAccountType } from "@/hooks/use-account-type";
import { TeacherTopBar } from "@/components/TeacherTopBar";
import { RoleSwitcherMenu } from "@/components/RoleSwitcher";
import { useWidgetSync } from "@/hooks/use-widget-sync";
import { useWidgetDeepLinks } from "@/hooks/use-widget-deep-links";
import { canAccess, homeForRole, isRole, normalizeRoleList, type AccountRole } from "@/lib/role-access";
import { AuthRequired } from "@/components/AuthRequired";
import { safeNextPath } from "@/lib/auth-redirect";
import { RouteError } from "@/components/route-states";

/** Solo gli errori di sessione mostrano la schermata "accesso richiesto". */
function isAuthError(error: unknown): boolean {
  const e = error as { status?: number; message?: string } | null;
  if (!e) return false;
  if (e.status === 401 || e.status === 403) return true;
  const msg = (e.message ?? "").toLowerCase();
  return (
    msg.includes("jwt") ||
    msg.includes("not authenticated") ||
    msg.includes("unauthorized") ||
    msg.includes("auth session missing")
  );
}
import { useEffect, useState } from "react";


export const Route = createFileRoute("/_app")({
  ssr: false,
  errorComponent: ({ error, reset }) => (isAuthError(error) ? <AuthRequired /> : <RouteError error={error} reset={reset} />),
  beforeLoad: async ({ location }) => {
    const next = safeNextPath(location.href);
    const { data } = await supabase.auth.getUser();
    if (!data.user) {
      throw redirect({ to: "/auth", search: next ? { next } : undefined });
    }
    const { data: profile } = await supabase
      .from("profiles")
      .select("onboarding_completed,account_type,account_roles,is_adult")
      .eq("id", data.user.id)
      .maybeSingle();
    if (profile && !profile.onboarding_completed) {
      throw redirect({ to: "/onboarding" });
    }
    const row = (profile ?? {}) as Record<string, unknown>;
    const roles = normalizeRoleList(row["account_roles"], Boolean(row["is_adult"]), row["account_type"]);
    const raw = row["account_type"];
    const role: AccountRole = isRole(raw) && roles.includes(raw) ? raw : roles[0]!;
    // La home cambia in base al ruolo: docente e genitore hanno la loro area.
    if (location.pathname === "/") {
      const home = homeForRole(role);
      if (home !== "/") throw redirect({ to: home });
    } else if (!canAccess(location.pathname, role)) {
      // Il gate della UI: le server function hanno un controllo di ruolo proprio.
      throw redirect({ to: homeForRole(role) });
    }
    return { user: data.user };

  },
  component: AppLayout,
});

function AppLayout() {
  const { t } = useTranslation();
  const skipLabel = t("a11y.skipToContent", { defaultValue: "Vai al contenuto" });
  // Guard globale lato client: se la sessione sparisce (logout, scadenza, altra
  // scheda) non mostriamo mai la shell vuota.
  const [signedOut, setSignedOut] = useState(false);
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setSignedOut(!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedOut(!session);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  const { focus } = useProfileFlags();
  const account = useAccountType();
  useTomorrowHomeworkReminder();
  useWidgetSync();
  useWidgetDeepLinks();
  const pro = account !== "student";
  if (signedOut) return <AuthRequired />;
  return (
    <div className={pro ? "min-h-dvh bg-muted/30 safe-x" : "min-h-dvh bg-background safe-x"}>
      <a href="#main-content" className="skip-link text-sm font-semibold">
        {skipLabel}
      </a>
      {account === "teacher" && <TeacherTopBar />}
      <div className={`mx-auto w-full ${pro ? "max-w-md sm:max-w-3xl lg:max-w-6xl" : "max-w-md sm:max-w-2xl lg:max-w-4xl"} pt-[max(env(safe-area-inset-top),0.5rem)] pb-[calc(env(safe-area-inset-bottom)+7rem)]`}>
        {account !== "teacher" && (
          <div className="flex justify-end px-4 pt-2 empty:hidden">
            <RoleSwitcherMenu />
          </div>
        )}
        <main id="main-content" tabIndex={-1} className="outline-none">
          <Outlet />
        </main>
      </div>

      <FocusBar />
      {!focus && <BottomNav />}
    </div>
  );
}